import React from 'react';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { ExternalLink } from 'lucide-react';

const BOOK_1_IMAGE = 'https://customer-assets.emergentagent.com/job_presence-design-1/artifacts/0f6w93mc_1739773765.jpg';
const BOOK_2_IMAGE = 'https://customer-assets.emergentagent.com/job_presence-design-1/artifacts/z86r0okp_1768451518.jpg';

const books = [
  {
    id: 1,
    title: 'Dream, Design, Deliver',
    subtitle: 'Crack Your Success Code',
    image: BOOK_1_IMAGE,
    description: 'A comprehensive guide to unlocking your potential and designing the success you envision.',
    color: '#c9a87c',
    link: 'https://www.amazon.in/Dream-Design-Deliver-Crack-Success/dp/9355620926?s=bazaar'
  },
  {
    id: 2,
    title: 'The Premier CEO Upgrade',
    subtitle: 'Untold Rules For Conscious Leadership In The Age of AI',
    image: BOOK_2_IMAGE,
    description: 'Essential principles for leaders navigating the complexities of modern business and technology.',
    color: '#5a7fa8',
    link: 'https://www.amazon.in/Premier-CEO-Upgrade-Conscious-Leadership/dp/937573062X/'
  }
];

export const BooksSection = () => {
  const [titleRef, titleVisible] = useScrollAnimation({ threshold: 0.2 });
  const [booksRef, booksVisible] = useScrollAnimation({ threshold: 0.15 });

  return (
    <section className="bg-[#fffef2] py-24 lg:py-32 relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#e8e7da] to-transparent" />
      
      {/* Floating decorative elements */}
      <div className="absolute top-20 right-16 w-32 h-32 border border-[#5a7fa8]/10 rounded-full float-slow" />
      <div className="absolute bottom-20 left-20 w-24 h-24 border border-[#c9a87c]/10 rounded-full float-reverse" />
      
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
        {/* Section Header */}
        <div
          ref={titleRef}
          className={`text-center mb-16 lg:mb-20 animate-on-scroll ${titleVisible ? 'is-visible' : ''}`}
        >
          <p className="text-xs text-[#5a7fa8] uppercase tracking-[0.3em] mb-4">Published Works</p>
          <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-[#333333] leading-tight mb-6">
            Books by the Author
          </h2>
          <p className="text-base text-[#666666] max-w-xl mx-auto">
            Transformative insights distilled into written form for leaders seeking depth.
          </p>
        </div>

        {/* Books Grid */}
        <div
          ref={booksRef}
          className={`grid md:grid-cols-2 gap-8 lg:gap-12 stagger-children ${booksVisible ? 'is-visible' : ''}`}
        >
          {books.map((book) => (
            <div
              key={book.id}
              className="group relative"
            >
              <div className="flex flex-col lg:flex-row gap-8 p-6 lg:p-8 bg-gradient-to-br from-[#1a2435] to-[#0f1a2a] border border-[#2a3a4d] transition-all duration-500 hover:border-opacity-50 hover-lift overflow-hidden"
                   style={{ '--accent-color': book.color }}
              >
                {/* Glow effect on hover */}
                <div 
                  className="absolute top-0 right-0 w-64 h-64 opacity-0 group-hover:opacity-20 blur-[60px] transition-opacity duration-700 rounded-full"
                  style={{ backgroundColor: book.color }}
                />
                
                {/* Book Cover */}
                <div className="relative flex-shrink-0 lg:w-48">
                  {/* Frame decoration */}
                  <div className="absolute -top-2 -left-2 w-8 h-8 border-t border-l transition-all duration-500 group-hover:w-12 group-hover:h-12"
                       style={{ borderColor: book.color + '50' }} />
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 border-b border-r transition-all duration-500 group-hover:w-12 group-hover:h-12"
                       style={{ borderColor: book.color + '50' }} />
                  
                  {/* Book image */}
                  <a 
                    href={book.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative block overflow-hidden shadow-2xl"
                  >
                    <img
                      src={book.image}
                      alt={book.title}
                      className="w-full lg:w-48 h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    {/* Subtle overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0f1a2a]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </a>
                </div>

                {/* Book Details */}
                <div className="flex-1 flex flex-col justify-center relative z-10">
                  <h3 className="font-serif text-xl lg:text-2xl text-[#f5f5f0] mb-2 transition-colors duration-300">
                    <span className="group-hover:text-[var(--accent-color)] transition-colors duration-300"
                          style={{ '--accent-color': book.color }}>
                      {book.title}
                    </span>
                  </h3>
                  <p className="text-sm text-[#b8c5d4] mb-4 leading-relaxed">
                    {book.subtitle}
                  </p>
                  <p className="text-sm text-[#6b7c93] leading-relaxed mb-6">
                    {book.description}
                  </p>
                  
                  {/* CTA Button */}
                  <div>
                    <a 
                      href={book.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm transition-all duration-300 group/btn"
                      style={{ color: book.color }}
                    >
                      <span className="relative">
                        Buy on Amazon
                        <span className="absolute bottom-0 left-0 w-0 h-px group-hover/btn:w-full transition-all duration-300"
                              style={{ backgroundColor: book.color }} />
                      </span>
                      <ExternalLink size={14} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
                    </a>
                  </div>
                </div>

                {/* Bottom accent line */}
                <div 
                  className="absolute bottom-0 left-0 w-0 h-px group-hover:w-full transition-all duration-700"
                  style={{ background: `linear-gradient(90deg, transparent, ${book.color}, transparent)` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BooksSection;
